import { useContext, useEffect, useRef } from 'react';
import { WebSocketContext } from '../context/WebSocketContext';

export const WORK_ORDER_STAR_CHANGE_MESSAGE_TYPE = 'wo_star_change';

// What changed on the star. `reminder_due` is the reminder going off (its condition held);
// `reminder_skipped` is it coming due with its condition false: no email or push, reminder cleared.
export const STAR_CHANGE_ACTION = {
	STARRED: 'starred',
	UNSTARRED: 'unstarred',
	NOTE_UPDATED: 'note_updated',
	CONDITION_UPDATED: 'condition_updated',
	REMINDER_SET: 'reminder_set',
	REMINDER_UPDATED: 'reminder_updated',
	REMINDER_SNOOZED: 'reminder_snoozed',
	REMINDER_REMOVED: 'reminder_removed',
	REMINDER_STOPPED: 'reminder_stopped',
	REMINDER_SKIPPED: 'reminder_skipped',
	REMINDER_DUE: 'reminder_due',
} as const;

export type StarChangeAction = typeof STAR_CHANGE_ACTION[keyof typeof STAR_CHANGE_ACTION];

// What the backend pushes on every open socket of the user whose star changed, for any change
// to it, from any tab or device (app-level: no room join needed). The star fields are the star
// after the change; a missing one has been cleared.
export interface WorkOrderStarChangeMessage {
	messageType: typeof WORK_ORDER_STAR_CHANGE_MESSAGE_TYPE;
	workOrderId: number;
	action: StarChangeAction;
	starNote?: string | null;
	remindAt?: string | null;
	// The reminder's "Remind me only if…".
	remindCondition?: string | null;
	changedAt: string;
	// How many of the user's reminders are due now: the Active Reminders filter's count.
	activeReminderCount?: number;
}

/**
 * Calls `onStarChange` for every change to one of the current user's stars over the WebSocket,
 * and once with no message after each reconnect, since missed messages aren't replayed.
 */
const useWorkOrderStarSocket = (
	onStarChange: (message: WorkOrderStarChangeMessage | null) => void
): void => {
	const socketContext = useContext(WebSocketContext);
	const socket = socketContext ? socketContext.socket : null;
	// The latest callback, so a re-render with a new closure doesn't re-attach the listener.
	const onStarChangeRef = useRef(onStarChange);
	onStarChangeRef.current = onStarChange;
	// The first socket attempt this hook saw: the connection the caller's mount-time load matches.
	const firstSocketRef = useRef(null);

	useEffect(() => {
		if (!socket) return undefined;
		const handleMessage = (event: MessageEvent) => {
			if (typeof event.data !== 'string' || event.data.charAt(0) !== '{') return;
			let message;
			try {
				message = JSON.parse(event.data);
			} catch (error) {
				return;
			}
			if (message && message.messageType === WORK_ORDER_STAR_CHANGE_MESSAGE_TYPE) {
				onStarChangeRef.current(message);
			}
		};
		const isFirstSocket = !firstSocketRef.current;
		if (isFirstSocket) firstSocketRef.current = socket;
		// Catch up once whenever a socket opens after we subscribed (reconnects, retries, or a first
		// socket still connecting at mount); a first socket already open is covered by the mount load.
		const markOpened = () => onStarChangeRef.current(null);
		socket.addEventListener('message', handleMessage);
		if (socket.readyState === WebSocket.OPEN) {
			if (!isFirstSocket) markOpened();
		} else {
			socket.addEventListener('open', markOpened);
		}
		return () => {
			socket.removeEventListener('message', handleMessage);
			socket.removeEventListener('open', markOpened);
		};
	}, [socket]);
};

// A message that puts a new notification in the Inbox, or a reconnect (null) that may have missed one.
export const isReminderDueOrReconnect = (message: WorkOrderStarChangeMessage | null): boolean =>
	!message || message.action === STAR_CHANGE_ACTION.REMINDER_DUE;

export default useWorkOrderStarSocket;
